/* eslint-disable global-require */
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  MenuItem,
  TooltipProps,
  Tooltip as MaterialTooltip,
  styled,
  SelectChangeEvent,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { Grid, Tooltip, tooltipClasses } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";

import { Input, Select } from "../../../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../../../components/Form/AutoSuggest";
import Button, { Color } from "../../../../../../../../../components/Form/Button";
import GeneralCommentsComponent from "../../../../../../../../../components/Generic/GeneralComments";
import PaginateFooter from "../../../../../../../../../components/Paginate";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { useTransshipmentState } from "../../../../../../../../../context/TransshipmentContext";
import { useActionTransshipment } from "../../../../../../../../../hooks/useActionTransshipment";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { GeneralComments } from "../../../../../../../../../models/general.comments";
import { LiftvanConfig } from "../../../../../../../../../models/liftvan.config";
import { Order } from "../../../../../../../../../models/order";
import { ItemJob } from "../../../../../../../../../models/sales";
import { Staff } from "../../../../../../../../../models/staff";
import { StorageBox, StorageBoxDto } from "../../../../../../../../../models/storage.box";
import {
  Transshipment,
  TransshipmentMovement,
} from "../../../../../../../../../models/transshipment";
import { TransshipmentComment } from "../../../../../../../../../models/transshipment.comment";
import { User } from "../../../../../../../../../models/user";
import { calculateTotalVolume } from "../../../../../../../../../util/calculateTotalVolume";
import {
  ItemJobSubStatus,
  TaskStatus,
  TransshipmentStatus,
  TransshipmentWhatToDo,
  TransshipmentWhereToLoad,
} from "../../../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../../../util/getMeasurement";
import { getStaffDescription } from "../../../../../../../../../util/getStaffDescription";
import { paginate } from "../../../../../../../../../util/paginate";
import SingleUseBoxModal, {
  SingleUseBoxModalHandle,
} from "../../../../../../Operations/Maintenance/Inventory/components/SingleUseBoxModal";
import CardContainer from "./CardContainer";
import {
  CardContainerTitle,
  DisabledWhereOption,
  IconContainer,
  InfoContainer,
  InfoText,
  InfoTitle,
  RemoveIcon,
  SingleUseInfoBox,
  StorageBoxOption,
  StorageBoxOptionNumber,
  StorageBoxOptionType,
  TextContainer,
  Title,
  TooltipContentWrapper,
} from "./styles";

const TASK_STATUS_NOT_ALLOWED = [
  TaskStatus.IN_PROGRESS.toLowerCase(),
  TaskStatus.STARTED.toLowerCase(),
  TaskStatus.PUBLISHED.toLowerCase(),
];

const Details = () => {
  const { t: translate } = useTranslation();

  const { warehouseList, warehouse } = useAppState();

  const measurement = getMeasurement();

  const {
    statuses,
    whatsToDo,
    whereToLoad,
    searchTypes,
    leaders,
    storageBoxes,
    storageBoxesFleets,
    storageBoxTypes,
    transshipment,
    setTransshipment,
    errors,
    transshipmentTypes = [],
    orders = [],
    external,
    setExternal,
    type,
    setType,
    reference,
    setReference,
    storageBox,
    setStorageBox,
    staffs = [],
    isDisabledToEdit,
    countOrders,
    setCountOrders,
    setOrders,
  } = useTransshipmentState();

  const [pageOrdersSearch, setPageOrdersSearch] = useState(1);
  const [pageOrdersAdded, setPageOrdersAdded] = useState(1);

  const { searchOrders, isSearching, pageSize } = useActionTransshipment();

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const singleUseBoxModalRef = useRef<SingleUseBoxModalHandle>(null);

  const INFO_CIRCLE_ICON = require("../../../../../../../../../assets/icons/info-circle.svg");
  const INFO_CIRCLE_2_ICON = require("../../../../../../../../../assets/icons/info-circle-2.svg");
  const TRASH_ICON = require("../../../../../../../../../assets/icons/trash.svg");

  useEffect(() => {
    if (pageOrdersAdded > 1) {
      const length = transshipment?.jobs?.length || 0;

      if (pageOrdersAdded === 2) {
        if (length <= pageSize) {
          setPageOrdersAdded(1);
        }
      } else {
        const totalLimitPreviousPage = pageOrdersAdded * pageSize - pageSize;

        if (length <= totalLimitPreviousPage) setPageOrdersAdded(pageOrdersAdded - 1);
      }
    }
  }, [transshipment?.jobs]);

  useEffect(() => {
    if (warehouse) {
      setTransshipment({
        ...(transshipment || {}),
        warehouseId: Number(warehouse?.id),
      } as Transshipment);
    }
  }, [warehouse]);

  useEffect(() => {
    if (transshipmentTypes && transshipmentTypes.length > 0 && !transshipment?.typeId) {
      const transshipmentTypeId = transshipmentTypes?.find(
        (transshipmentType) => transshipmentType.name === TransshipmentStatus.PLANNED
      )?.id;

      setTransshipment({ ...(transshipment || {}), typeId: Number(transshipmentTypeId) });
    }
  }, [transshipmentTypes, transshipment]);

  const onAddOrder = (order: Order, _index: number) => {
    const ordersAddedUpdated = [...(transshipment?.jobs || [])];

    const movements: TransshipmentMovement[] = [];

    if (order && order?.itemsJob && order?.itemsJob?.length) {
      order?.itemsJob?.forEach((itemJob) => {
        movements.push({
          itemJobId: Number(itemJob?.id),
          isExternal: external,
          storageBoxId: storageBox?.id,
          jobId: order?.id,
          referenceSearch: reference,
          typeSearch: type,
          whatToDo: transshipment?.whatToDo,
        } as TransshipmentMovement);
      });
    }

    ordersAddedUpdated.push({ ...order, movements });

    if (transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE) {
      const storageBoxesUpdated = {
        ...transshipment?.storageBoxes?.[0],
        singleUseJobId: order.id,
      } as StorageBox;

      setTransshipment({
        ...(transshipment || {}),
        jobs: ordersAddedUpdated,
        storageBoxes: [storageBoxesUpdated],
      });
    } else {
      setTransshipment({ ...(transshipment || {}), jobs: ordersAddedUpdated });
    }
  };

  const onRemoveOrder = (order: Order, index: number) => {
    const ordersAddedUpdated = [...(transshipment?.jobs || [])];

    ordersAddedUpdated.splice(index, 1);
    setTransshipment({ ...(transshipment || {}), jobs: ordersAddedUpdated });
  };

  const allItemsJobs = useMemo(() => {
    const itemsJobs: ItemJob[] = [];

    if (transshipment?.jobs) {
      transshipment?.jobs?.forEach((job) => {
        if (job?.itemsJob && job?.itemsJob?.length) {
          job?.itemsJob?.forEach((itemJob) => {
            itemsJobs.push(itemJob);
          });
        }
      });
    }

    return itemsJobs;
  }, [transshipment?.jobs]);

  const transshipmentAssignedLeader = useMemo(() => {
    if (transshipment?.leaderId && leaders && leaders?.length > 0) {
      const assignedLeader = leaders.find((leader) => leader?.id === transshipment?.leaderId);

      return assignedLeader;
    }

    return undefined;
  }, [transshipment?.leaderId, leaders]);

  const ordersAddedPaginate = useMemo(() => {
    return paginate(
      transshipment?.jobs || [],
      pageOrdersAdded > 0 ? pageOrdersAdded - 1 : 0,
      pageSize
    );
  }, [transshipment?.jobs, pageOrdersAdded, pageSize]);

  const cleanOrdersSearch = () => {
    setOrders([]);
    setPageOrdersSearch(1);
    setCountOrders(0);
  };

  const SingleUseStorageBoxInfoTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MaterialTooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      padding: "12px 16px",
      maxWidth: 312,
      background: colors.white,
      borderRadius: "12px",
      boxShadow: "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)",
    },
  }));

  const DisabledSingleUseTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MaterialTooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      padding: "4px 8px",
      maxWidth: 200,
      background: colors.gray600,
      borderRadius: "4px",
      font: "400 12px Manrope",
      color: colors.white,
      lineHeight: "16px",
      letterSpacing: "0.4px",
    },
  }));

  const renderAddedOrdersTitle = () => {
    if (transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE) {
      return (
        <CardContainerTitle>
          <strong>{transshipment?.jobs?.length || 0}/1</strong> {translate("added")}
        </CardContainerTitle>
      );
    }

    return (
      <CardContainerTitle>
        <strong>{transshipment?.jobs?.length || 0}</strong> {translate("added")}
      </CardContainerTitle>
    );
  };

  const renderTooltip = () => {
    if (transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE) {
      return (
        <SingleUseStorageBoxInfoTooltip
          title={
            <TooltipContentWrapper>
              <InfoTitle>{translate("storageBoxSingleUseSelected")}</InfoTitle>
              <InfoText>{translate("youCanAddOnlyOneOrderToAStorageBoxSingleUse")}</InfoText>
            </TooltipContentWrapper>
          }
          placement="top-end"
        >
          <InfoContainer>
            <img src={INFO_CIRCLE_2_ICON} width="16" height="16" alt="info icon" />
          </InfoContainer>
        </SingleUseStorageBoxInfoTooltip>
      );
    }

    return null;
  };

  const getLifvanConfigName = (liftvanConfig?: LiftvanConfig) => {
    if (liftvanConfig) {
      return `${liftvanConfig?.prefix} - 1 ${translate("of")} ${liftvanConfig?.howMany}`;
    }

    return "";
  };

  const getStorageBoxSingleUseType = (typeId?: number) => {
    if (typeId) {
      const type = storageBoxTypes?.find((type) => type.id === typeId);

      if (type) return type.name;
    }

    return "";
  };

  const renderStorageBoxSingleUseFields = () => {
    if (transshipment?.jobs && transshipment?.jobs?.length > 1) {
      return (
        <Grid item xs={12} sm={"auto"}>
          <SingleUseInfoBox>
            <IconContainer>
              <img src={INFO_CIRCLE_ICON} alt="info icon" height="20" width="20" />
            </IconContainer>
            <TextContainer>
              {translate("youCanOnlyUseStorageBoxSingleUseForJustOneOrder")}
            </TextContainer>
          </SingleUseInfoBox>
        </Grid>
      );
    }

    if (transshipment?.storageBoxes && transshipment?.storageBoxes?.length) {
      return (
        <>
          <Grid item xs={12} sm={3}>
            <Input
              name="container"
              value={
                transshipment?.storageBoxes?.[0]?.liftvanConfig
                  ? getLifvanConfigName(transshipment?.storageBoxes?.[0]?.liftvanConfig) || ""
                  : transshipment?.storageBoxes?.[0]?.number || ""
              }
              label={
                transshipment?.storageBoxes?.[0]?.liftvanConfig
                  ? translate("liftvan")
                  : getStorageBoxSingleUseType(transshipment?.storageBoxes?.[0]?.storageBoxTypeId)
              }
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <RemoveIcon
              onClick={() => {
                setTransshipment({
                  ...(transshipment || {}),
                  storageBoxes: [],
                });
              }}
            >
              <img src={TRASH_ICON} alt="remove icon" width="24" height="24" />
            </RemoveIcon>
          </Grid>
        </>
      );
    }

    return (
      <Grid item xs={12} sm={3}>
        <Button
          startIcon={<FiPlus size={20} />}
          customColor={Color.SECONDARY_OUTLINED}
          onClick={() => {
            singleUseBoxModalRef?.current?.open();
          }}
        >
          {translate("createNew")}
        </Button>
      </Grid>
    );
  };

  return (
    <Grid container alignItems="flex-start" item xs={12} spacing={2} direction="row">
      <Grid item xs={12}>
        <Title>{translate("generalDetails")}</Title>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Input
          required
          name="reference"
          value={transshipment?.reference}
          disabled
          label={translate("reference")}
          error={fieldHasError("reference")}
          errorMessage={getErrorByField("reference")}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Select
          required
          name="type"
          label={translate("type")}
          error={fieldHasError("typeId")}
          errorMessage={getErrorByField("typeId")}
          placeholder={translate("select")}
          value={transshipment?.typeId}
          onChange={(event) =>
            setTransshipment({ ...(transshipment || {}), typeId: Number(event.target.value) })
          }
          disabled
        >
          {transshipmentTypes && transshipmentTypes?.length > 0
            ? transshipmentTypes?.map((transshipmentType) => {
                return (
                  <MenuItem key={transshipmentType?.id} value={transshipmentType?.id}>
                    {transshipmentType?.name || ""}
                  </MenuItem>
                );
              })
            : null}
        </Select>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Input
          required
          type="date"
          name="dueBy"
          min={new Date()}
          value={
            transshipment?.dueBy
              ? format(
                  utcToZonedTime(
                    transshipment?.dueBy,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "yyyy-MM-dd"
                )
              : ""
          }
          onChange={(event) => {
            if (event?.target?.value)
              setTransshipment({
                ...(transshipment || {}),
                dueBy: zonedTimeToUtc(
                  event.target.value,
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                ),
              });
          }}
          label={translate("dueDateBy")}
          error={fieldHasError("dueBy")}
          errorMessage={getErrorByField("dueBy")}
          disabled={isDisabledToEdit}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Select
          required
          name="status"
          value={transshipment?.status}
          onChange={(event) =>
            setTransshipment({
              ...(transshipment || {}),
              status: event.target.value,
            } as Transshipment)
          }
          label={translate("status")}
          error={fieldHasError("status")}
          errorMessage={getErrorByField("status")}
          placeholder={translate("select")}
          disabled
        >
          {statuses &&
            statuses?.length > 0 &&
            statuses?.map((status) => {
              return (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              );
            })}
        </Select>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Select
          required
          name="warehouse"
          label={translate("warehouse")}
          value={transshipment?.warehouseId}
          disabled
          onChange={(event) => {
            setTransshipment({
              ...(transshipment || {}),
              warehouseId: Number(event.target.value),
            } as Transshipment);

            cleanOrdersSearch();
          }}
          error={fieldHasError("warehouseId")}
          errorMessage={getErrorByField("warehouseId")}
          placeholder={translate("select")}
        >
          {warehouseList &&
            warehouseList?.length > 0 &&
            warehouseList?.map((warehouse) => (
              <MenuItem key={warehouse?.id} value={warehouse?.id}>
                {warehouse?.name}
              </MenuItem>
            ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Select
          disabled
          name="requestedBy"
          label={translate("requestedBy")}
          value={transshipment?.createdBy?.fullName || translate("system")}
          placeholder={translate("select")}
        >
          <MenuItem value={transshipment?.createdBy?.fullName || translate("system")}>
            {transshipment?.createdBy?.fullName || translate("system")}
          </MenuItem>
        </Select>
      </Grid>
      <Grid item xs={12}>
        <Title>{translate("team")}</Title>
      </Grid>
      <Grid item xs={12} sm={3}>
        <AutoSuggest
          name="assigned"
          label={translate("assigned")}
          placeholder={translate("select")}
          getOptionLabel={(leader?: User) => {
            if (leader) return `${leader?.fullName} - ${leader?.email ?? ""}` || "";

            return "";
          }}
          getOptionSelected={(option: User, value: User) => option?.id === value?.id}
          inputValue={`${transshipmentAssignedLeader?.fullName ?? ""}${
            transshipmentAssignedLeader?.email ? ` - ${transshipmentAssignedLeader?.email}` : ""
          }`}
          value={transshipmentAssignedLeader}
          onChange={(leader: User) =>
            setTransshipment({ ...(transshipment || {}), leaderId: leader?.id })
          }
          options={leaders && leaders.length > 0 ? leaders : []}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <AutoSuggest
          label={translate("staff")}
          placeholder={!transshipment?.staffs?.length ? `${translate("searchForStaff")}...` : ""}
          getOptionLabel={getStaffDescription}
          getOptionSelected={(option: Staff, value: Staff) => option?.id === value?.id}
          value={
            transshipment?.staffs && transshipment?.staffs.length > 0 ? transshipment?.staffs : []
          }
          multiple
          onChange={(staffs: Staff[]) => setTransshipment({ ...(transshipment || {}), staffs })}
          options={staffs && staffs?.length > 0 ? staffs : []}
          disabled={isDisabledToEdit}
        />
      </Grid>
      <Grid item xs={12}>
        <Title>{translate("resources")}</Title>
      </Grid>
      <Grid item container alignItems="flex-end" spacing={2}>
        <Grid item xs={12} sm={3}>
          <Select
            disabled={isSearching || isDisabledToEdit}
            name="whatToDo"
            required
            label={translate("whatToDo")}
            value={transshipment?.whatToDo}
            onChange={(event: SelectChangeEvent<any>) => {
              const whatToDoUpdated = event.target.value;

              if (whatToDoUpdated === TransshipmentWhatToDo.UNLOAD) {
                setExternal(undefined);
                setStorageBox(undefined);
              } else if (external === undefined) setExternal(false);

              setTransshipment({
                ...(transshipment || {}),
                whatToDo: whatToDoUpdated,
                whereLoad: undefined,
              });
            }}
            placeholder={translate("select")}
            error={fieldHasError("whatToDo")}
            errorMessage={getErrorByField("whatToDo")}
          >
            {whatsToDo &&
              whatsToDo?.length > 0 &&
              whatsToDo?.map((description) => {
                return (
                  <MenuItem key={description} value={description}>
                    {description}
                  </MenuItem>
                );
              })}
          </Select>
        </Grid>
        {transshipment?.whatToDo === TransshipmentWhatToDo.LOAD ? (
          <Grid item xs={12} sm={3}>
            <Select
              disabled={isSearching || isDisabledToEdit}
              name="where"
              label={translate("where")}
              value={transshipment?.whereLoad}
              onChange={(event: SelectChangeEvent<any>) => {
                if (event?.target?.value) {
                  setStorageBox(undefined);
                  setTransshipment({
                    ...(transshipment || {}),
                    whereLoad: event.target.value,
                    storageBoxes: [],
                  });
                }
              }}
              placeholder={translate("select")}
            >
              {whereToLoad &&
                whereToLoad?.length > 0 &&
                whereToLoad?.map((description) => {
                  if (
                    description === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE &&
                    transshipment?.jobs &&
                    transshipment?.jobs?.length > 1
                  ) {
                    return (
                      <DisabledSingleUseTooltip
                        title={translate("youCanOnlyUseOneStorageBoxSingleUsePerOrder")}
                        placement="top"
                      >
                        <DisabledWhereOption>
                          <MenuItem key={description} value={description} disabled>
                            {description}
                          </MenuItem>
                        </DisabledWhereOption>
                      </DisabledSingleUseTooltip>
                    );
                  }

                  return (
                    <MenuItem key={description} value={description}>
                      {description}
                    </MenuItem>
                  );
                })}
            </Select>
          </Grid>
        ) : null}
        {transshipment?.whereLoad === TransshipmentWhereToLoad.FLEET ? (
          <Grid item xs={12} sm={3}>
            <AutoSuggest
              disabled={isSearching || isDisabledToEdit}
              name="fleet"
              label={translate("fleet")}
              getOptionLabel={(storageBoxFleet: StorageBox) => storageBoxFleet?.number || ""}
              getOptionSelected={(option: StorageBox, value: StorageBox) =>
                option?.id === value?.id
              }
              value={transshipment?.storageBoxes?.[0]}
              inputValue={transshipment?.storageBoxes?.[0]?.number ?? ""}
              onChange={(storageBoxFleet: StorageBox) => {
                setTransshipment({
                  ...(transshipment || {}),
                  storageBoxes: [storageBoxFleet],
                });
              }}
              options={
                storageBoxesFleets && storageBoxesFleets?.length > 0 ? storageBoxesFleets : []
              }
              placeholder={translate("select")}
            />
          </Grid>
        ) : null}
        {transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX ? (
          <Grid item xs={12} sm={3}>
            <AutoSuggest
              disabled={isSearching || isDisabledToEdit}
              name="storageBox"
              label={translate("storageBox")}
              getOptionLabel={(storageBox: StorageBox) => storageBox?.number || ""}
              getOptionSelected={(option: StorageBox, value: StorageBox) =>
                option?.id === value?.id
              }
              renderOption={(props: any, option: StorageBox) => (
                <div {...props}>
                  <StorageBoxOption>
                    <StorageBoxOptionNumber>{option.number}</StorageBoxOptionNumber>
                    <StorageBoxOptionType>{option.storageBoxType?.name}</StorageBoxOptionType>
                  </StorageBoxOption>
                </div>
              )}
              value={transshipment?.storageBoxes?.[0]}
              inputValue={transshipment?.storageBoxes?.[0]?.number ?? ""}
              onChange={(storageBox: StorageBox) => {
                setTransshipment({
                  ...(transshipment || {}),
                  storageBoxes: [storageBox],
                });
              }}
              options={storageBoxes && storageBoxes?.length > 0 ? storageBoxes : []}
              placeholder={translate("select")}
            />
          </Grid>
        ) : null}
        {transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE
          ? renderStorageBoxSingleUseFields()
          : null}
      </Grid>
      <Grid item container alignItems="flex-end" spacing={2}>
        <Grid item xs={12} sm={3}>
          <Select
            name="type"
            value={type}
            onChange={(event: SelectChangeEvent<any>) => {
              setType(event.target.value);
              cleanOrdersSearch();
            }}
            label={translate("type")}
            placeholder={translate("select")}
            disabled={isSearching || isDisabledToEdit}
          >
            {searchTypes &&
              searchTypes?.length > 0 &&
              searchTypes?.map((type) => {
                return (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                );
              })}
          </Select>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Input
            name="reference"
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
              cleanOrdersSearch();
            }}
            label={translate("reference")}
            disabled={isSearching || isDisabledToEdit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            disabled={
              // eslint-disable-next-line no-nested-ternary
              isDisabledToEdit ? true : !type || !reference
            }
            onClick={() => {
              searchOrders({
                warehouseId: Number(transshipment?.warehouseId),
                type,
                reference,
                page: 0,
              });
              setPageOrdersSearch(1);
            }}
            customColor={Color.SECONDARY}
            loading={isSearching}
          >
            {translate("search")}
          </Button>
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={1} direction="row">
        <Grid item xs={12} sm={6}>
          <CardContainer
            hideView={(order) => {
              if (
                isDisabledToEdit ||
                !!(transshipment?.jobs || []).find((job) => job?.id === order?.id) ||
                !!(!order?.itemsJob || order?.itemsJob?.length === 0) ||
                !!order?.tasksJob?.filter((task) =>
                  TASK_STATUS_NOT_ALLOWED.includes(task.status.toLowerCase() as TaskStatus)
                )?.length
              ) {
                return true;
              }

              return false;
            }}
            title={
              countOrders && countOrders > 1 ? (
                <CardContainerTitle>
                  <strong>{countOrders}</strong> {translate("results")}
                </CardContainerTitle>
              ) : (
                <CardContainerTitle>
                  <strong>{countOrders || 0}</strong> {translate("result")}
                </CardContainerTitle>
              )
            }
            orders={orders}
            onSaveOrder={(order) => {
              const jobsTransshipmentUpdated = [...(transshipment?.jobs || [])];

              jobsTransshipmentUpdated.push(order);
              setTransshipment({ ...transshipment, jobs: jobsTransshipmentUpdated });
            }}
            renderButton={(order, index) => {
              const hasNotItems = !!(!order?.itemsJob || order?.itemsJob?.length === 0);
              const hasAdded = !!(transshipment?.jobs || []).find((job) => job?.id === order?.id);
              const hasTaskTransshipment = !!order?.tasksJob?.filter((task) =>
                TASK_STATUS_NOT_ALLOWED.includes(task.status.toLowerCase() as TaskStatus)
              )?.length;
              const hasOrderInStorageBoxSingleUse = !!(
                transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE &&
                Number(transshipment?.jobs?.length) > 0
              );

              const disabled =
                hasAdded ||
                hasNotItems ||
                isDisabledToEdit ||
                hasTaskTransshipment ||
                hasOrderInStorageBoxSingleUse;

              let title = "";

              if (hasNotItems) title = translate("noItemsToAddToTransshipment");
              if (hasAdded) title = translate("theOrderHasAlreadyBeenAdded");
              if (hasTaskTransshipment) title = translate("theOrderAlreadyIsInATransshipment");

              return (
                <Tooltip placement="top" title={title}>
                  <div>
                    <Button disabled={disabled} onClick={() => onAddOrder(order, index)}>
                      <AddIcon />
                      &nbsp;
                      {translate("add")}
                    </Button>
                  </div>
                </Tooltip>
              );
            }}
            isLoading={isSearching}
            paginate={
              countOrders > pageSize ? (
                <PaginateFooter
                  page={pageOrdersSearch}
                  onChangePage={(page) => {
                    setPageOrdersSearch(page);
                    searchOrders({ type, reference, page: page - 1 });
                  }}
                  total={countOrders}
                  pageSize={pageSize}
                />
              ) : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CardContainer
            required
            hasError={fieldHasError("jobs")}
            hideView={() => isDisabledToEdit || false}
            title={renderAddedOrdersTitle()}
            info={translate("planned")}
            tooltip={renderTooltip()}
            volume={
              <CardContainerTitle>
                <strong>
                  {calculateTotalVolume(ItemJobSubStatus.ACTUAL, measurement, allItemsJobs)}
                </strong>
              </CardContainerTitle>
            }
            orders={ordersAddedPaginate}
            onSaveOrder={(order) => {
              const jobsTransshipmentUpdated = [...(transshipment?.jobs || [])];
              const index = jobsTransshipmentUpdated.findIndex((job) => job?.id === order?.id);

              if (index !== -1) {
                if (order?.movements && order?.movements?.length) {
                  jobsTransshipmentUpdated[index] = order;
                  setTransshipment({ ...transshipment, jobs: jobsTransshipmentUpdated });
                } else {
                  onRemoveOrder(order, index);
                }
              }
            }}
            renderButton={(order, index) => (
              <Button
                variant="text"
                customColor={Color.CANCEL}
                onClick={() => onRemoveOrder(order, index)}
                disabled={isDisabledToEdit}
              >
                {translate("remove")}
              </Button>
            )}
            paginate={
              (transshipment?.jobs?.length || 0) > pageSize ? (
                <PaginateFooter
                  page={pageOrdersAdded}
                  onChangePage={(page) => {
                    setPageOrdersAdded(page);
                  }}
                  total={transshipment?.jobs?.length || 0}
                  pageSize={pageSize}
                />
              ) : undefined
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <GeneralCommentsComponent
          isViewMode={isDisabledToEdit}
          comments={
            transshipment?.comments
              ? transshipment?.comments?.map((comment) => {
                  return {
                    ...comment,
                    user: comment?.createdBy,
                    userId: comment?.createdById,
                  } as GeneralComments;
                })
              : []
          }
          generalCommentsTitle={translate("comments")}
          onAddComment={(comment) => {
            const comments = transshipment?.comments || [];

            comments.push({
              ...comment,
              createdBy: {
                name: comment?.user?.name,
                lastName: comment?.user?.lastName,
                fullName: comment?.user?.fullName,
                picture: { mediaLink: comment?.user?.picture?.mediaLink },
              },
              createdById: comment?.userId,
              userId: null,
              user: null,
            } as unknown as TransshipmentComment);
            setTransshipment({ ...(transshipment || {}), comments });
          }}
          onRemoveComment={(commentsUpdated) =>
            setTransshipment({
              ...(transshipment || {}),
              comments: commentsUpdated as TransshipmentComment[],
            })
          }
        />
      </Grid>
      <SingleUseBoxModal
        job={
          transshipment?.whereLoad === TransshipmentWhereToLoad.STORAGE_BOX_SINGLE_USE
            ? transshipment.jobs?.[0]
            : undefined
        }
        ref={singleUseBoxModalRef}
        showLeftActionButtons={false}
        isTransshipment={true}
        transshipment={transshipment}
        storageBoxTypesList={storageBoxTypes || []}
        onSubmit={(storageBox: StorageBoxDto) => {
          const storageBoxToSave: StorageBox = {
            ...storageBox,
            isActive: true,
            warehouseId: warehouse?.id || 0,
          };

          setTransshipment({
            ...(transshipment || {}),
            storageBoxes: [storageBoxToSave],
          });
        }}
        onSubmitLiftvan={(storageBox: StorageBoxDto, liftvanConfig: LiftvanConfig) => {
          const storageBoxToSave: StorageBox = {
            ...storageBox,
            isActive: true,
            warehouseId: warehouse?.id || 0,
            liftvanConfig,
            number: liftvanConfig.prefix || "",
          };

          setTransshipment({
            ...(transshipment || {}),
            storageBoxes: [storageBoxToSave],
          });
        }}
      />
    </Grid>
  );
};

export default Details;
